// Intégration du chat de support Crisp : visibilité, identité, actions.
import { useCallback, useEffect } from 'react';
import posthog from 'posthog-js';

import { isPublicAuthPath } from '@/lib/publicAuthPaths';

declare global {
  interface Window {
    $crisp: unknown[];
    CRISP_WEBSITE_ID: string;
  }
}

/** Ce que Crisp a besoin de savoir de l'utilisateur connecté. */
interface CrispSessionUser {
  id: string;
  email?: string | null;
  fullName?: string | null;
}

// Helper to safely push commands to Crisp
const pushToCrisp = (command: unknown[]) => {
  try {
    if (typeof window !== 'undefined' && window.$crisp) {
      window.$crisp.push(command);
    }
  } catch (error) {
    // Silently fail
  }
};

// Check if Crisp is fully loaded and ready
const isCrispLoaded = (): boolean => {
  return typeof window !== 'undefined' && window.$crisp !== undefined;
};

/* -------------------------------------------------------------------------- *
 * Visibilité du chatbox Crisp — une autorisation, N réservations de masquage
 * -------------------------------------------------------------------------- */

/**
 * `chat:show` / `chat:hide` est un interrupteur **global et sans compteur**.
 * Tant que chaque surface l'actionnait directement, le DERNIER écrivain gagnait
 * même quand il n'était plus le bon (bug produit 2026-08-14) : le dock assistant
 * cachait le launcher pour dégager son bouton d'envoi, puis l'ouverture-fermeture
 * du panneau bordereau appelait `showWidget()` dans son nettoyage et faisait
 * réapparaître la bulle Crisp par-dessus ce même bouton. Une simple ré-exécution
 * d'`AuthProvider` produisait le même effet.
 *
 * Remède : le même que celui des scroll-locks empilés de Radix (cf. la section
 * « Fullscreen dialogs » de `apps/legalworkspace/CLAUDE.md`) — personne ne
 * décide seul de l'état final, chacun déclare son besoin :
 *
 * - **autorisation de session**, propriété exclusive d'`AuthProvider` (via
 *   `syncCrispSupportSession`) : pas de canal support pour un visiteur non
 *   connecté, ni sur une page publique hors application (inscription,
 *   connexion, complétion de profil) ;
 * - **réservations de masquage**, prises par chaque surface qui recouvrirait le
 *   chatbox. Elles sont ref-comptées : libérer la sienne ne décide jamais pour
 *   les autres.
 *
 * Le chatbox n'est visible que si la session l'autorise ET qu'aucune réservation
 * n'est en cours. Aucune surface n'appelle plus `chat:show`/`chat:hide` en direct.
 */
let isChatboxAuthorized = false;

let nextHideTicketId = 0;

/** Réservations en cours : id → étiquette du demandeur (diagnostic uniquement). */
const chatboxHideTickets = new Map<number, string>();

/**
 * Dernier état effectivement poussé à Crisp. Initialisé à `false` parce que
 * `index.html` pousse `["do", "chat:hide"]` avant même le boot React : au
 * premier render, le chatbox est déjà caché.
 */
let appliedChatboxVisible = false;

/**
 * Fenêtre étroite = sous le breakpoint `md` de Tailwind (768 px), le seuil où la
 * coque passe à la barre de navigation du bas.
 *
 * Pourquoi masquer la bulle à cette largeur (triage 2026-10-19, deux avocates sur
 * iPhone) : la seule bulle de chat visible était celle du support, nommée
 * « Hector » dans Crisp, et des questions juridiques destinées à l'agent y
 * arrivaient. Elle recouvrait aussi le bouton d'envoi et l'onglet Profil. Le
 * support reste joignable par « Contacter le support » du menu Profil, qui
 * passe par `openChat` ci-dessous.
 *
 * Critère de LARGEUR, pas d'appareil : l'option Crisp `hide:on:mobile` détecte
 * le type d'appareil, alors qu'une fenêtre étroite doit se comporter pareil sur
 * Chrome comme sur Safari (consigne produit 2026-10-22).
 * Source (`hide:on:mobile`, événement `chat:closed`) :
 * https://docs.crisp.chat/guides/chatbox-sdks/web-sdk/dollar-crisp/ (vérifié 2026-10-22)
 */
const NARROW_WINDOW_QUERY = '(max-width: 767px)';

/** Le support a été demandé explicitement : la fenêtre Crisp s'affiche jusqu'à sa fermeture. */
let isSupportRequested = false;

/**
 * UN seul `MediaQueryList` par onglet : chaque appel à `matchMedia` en crée un
 * nouveau (spec CSSOM), et réécouter à chaque synchronisation doublait les
 * écouteurs à chaque passage du seuil (rotation d'un téléphone) jusqu'au gel.
 * `undefined` = pas encore créé ; `null` = pas de `matchMedia` (jsdom).
 */
let narrowWindowQuery: MediaQueryList | null | undefined;

const isNarrowWindow = (): boolean => {
  if (narrowWindowQuery === undefined) {
    narrowWindowQuery =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(NARROW_WINDOW_QUERY)
        : null;
    // Redimensionner fait franchir le seuil sans aucun rendu React. Safari < 14
    // n'a que `addListener`.
    const onChange = () => syncCrispChatbox();
    if (narrowWindowQuery?.addEventListener) narrowWindowQuery.addEventListener('change', onChange);
    else narrowWindowQuery?.addListener(onChange);
  }
  return narrowWindowQuery?.matches ?? false;
};

/**
 * Réconcilie Crisp avec l'état désiré. Ne pousse que sur transition — un
 * montage/démontage de panneau ne doit pas spammer la file de commandes.
 */
const syncCrispChatbox = ({ force = false }: { force?: boolean } = {}): void => {
  // Une demande explicite de support passe devant les réservations : en fenêtre
  // étroite la fenêtre Crisp ouverte couvre tout l'écran, il n'y a plus de
  // bouton à dégager, et l'ignorer laissait « Contacter le support » sans effet.
  const shouldBeVisible =
    isChatboxAuthorized &&
    (isSupportRequested || (chatboxHideTickets.size === 0 && !isNarrowWindow()));
  if (!force && shouldBeVisible === appliedChatboxVisible) return;

  // `window.$crisp` est une **file** installée de façon synchrone par
  // `index.html`, et Crisp documente `push` comme async-safe : « It uses a
  // queue-based syntax, which means you can safely push many commands before
  // the chatbox is fully loaded. » Le chargement asynchrone de `l.js` n'est
  // donc pas une course à gérer ici. Le seul cas où la file manque est un Crisp
  // bloqué (extension, CSP) : il n'y a alors aucun chatbox à cacher, et on ne
  // mémorise pas l'état pour que la prochaine transition ré-essaie.
  // Source: https://help.crisp.chat/en/article/how-to-use-the-crisp-chatbox-javascript-sdk-1lp254r/ (vérifié 2026-08-14)
  if (!isCrispLoaded()) return;

  appliedChatboxVisible = shouldBeVisible;
  // On ne ferme JAMAIS la conversation avant de cacher : Crisp spécifie
  // « Hides the chatbox (don't close it if open, sets it invisible) », donc une
  // conversation support en cours est masquée avec le reste et survit au
  // masquage. Un `chat:close` préalable détruirait l'état d'une conversation que
  // l'avocat n'a pas demandé à quitter.
  // Source: https://docs.crisp.chat/guides/chatbox-sdks/web-sdk/dollar-crisp/ (vérifié 2026-08-14)
  pushToCrisp(['do', shouldBeVisible ? 'chat:show' : 'chat:hide']);
};

/**
 * Autorise ou interdit le canal support.
 *
 * **Propriétaire unique : `AuthProvider`**, qui passe par
 * `syncCrispSupportSession` ci-dessous (c'est elle qui porte la règle
 * « session ouverte ET page de l'application »). Autoriser ne rend pas le chatbox
 * visible si une surface a une réservation de masquage en cours — c'est
 * précisément ce qui empêche un re-run d'auth de rouvrir la bulle par-dessus un
 * panneau ouvert.
 */
export const setCrispChatboxAuthorized = (authorized: boolean): void => {
  if (isChatboxAuthorized === authorized) return;
  isChatboxAuthorized = authorized;
  syncCrispChatbox();
};

/** Prend une réservation de masquage. Le retour la libère (idempotent). */
const acquireCrispChatboxHide = (owner: string): (() => void) => {
  const ticketId = nextHideTicketId++;
  chatboxHideTickets.set(ticketId, owner);
  syncCrispChatbox();

  let isReleased = false;
  return () => {
    // Idempotent : un cleanup React peut être rejoué (StrictMode double-invoque
    // les effets en dev), et une double libération volerait la réservation d'un
    // futur demandeur si les ids étaient recyclés.
    if (isReleased) return;
    isReleased = true;
    chatboxHideTickets.delete(ticketId);
    syncCrispChatbox();
  };
};

/**
 * Masque le chatbox Crisp tant que ce composant est monté (ou tant que `hidden`
 * vaut `true`), sans jamais décider pour les autres surfaces.
 *
 * Le launcher Crisp est en `position: fixed` en bas à droite du viewport : toute
 * surface qui pose une action à cet endroit (composer docké à droite, footer de
 * panneau) doit prendre une réservation plutôt que d'appeler `chat:hide`.
 *
 * @param owner Étiquette du demandeur, pour le diagnostic (`'assistant-dock'`…).
 * @param hidden Réservation active ? Par défaut `true` = « tant que je suis monté ».
 *
 * Ne retourne rien et ne prend que des primitives : contrairement aux fonctions
 * de `useCrisp()`, il n'y a aucune référence instable à mémoïser côté appelant,
 * donc pas de reprise possible de la boucle d'effets du 2026-06-21.
 */
export const useCrispChatboxHidden = (owner: string, hidden: boolean = true): void => {
  useEffect(() => {
    if (!hidden) return;
    return acquireCrispChatboxHide(owner);
  }, [hidden, owner]);
};

/* -------------------------------------------------------------------------- *
 * Session support — qui est identifié, et quand le canal est ouvert
 * -------------------------------------------------------------------------- */

/**
 * Identité actuellement portée par la session Crisp de l'onglet :
 * - `'unknown'` au chargement de la page — le cookie Crisp survit aux
 *   rechargements et peut encore porter le compte précédent ;
 * - `'anonymous'` une fois la session réinitialisée ;
 * - sinon l'id de l'utilisateur identifié.
 */
let crispIdentity: string = 'unknown';
let crispDiagnosticsRegistered = false;
let crispExpectedEmail: string | null = null;
let crispExpectedNickname: string | null = null;

const CRISP_ANONYMOUS = 'anonymous';
const CRISP_LAST_USER_KEY = 'hector.crisp.lastUserId';

const captureCrispEvent = (name: string, properties?: Record<string, unknown>): void => {
  try {
    posthog.capture(name, properties);
  } catch {
    // Support diagnostics must not interfere with Crisp's own event callbacks.
  }
};

/** Crisp can reload its own session after a reconnect without remounting Hector. */
const pushCrispIdentity = (userId: string, email: string, nickname: string | null): void => {
  pushToCrisp(['set', 'user:email', [email]]);
  if (nickname) pushToCrisp(['set', 'user:nickname', [nickname]]);
  // Crisp expects one outer array around the list of [key, value] pairs.
  // Source: https://docs.crisp.chat/guides/chatbox-sdks/web-sdk/dollar-crisp/ (verified 2026-10-13)
  pushToCrisp(['set', 'session:data', [[['user_id', userId]]]]);
};

/**
 * Crisp can swap to a new, anonymous session mid-page without emitting
 * `session:loaded` (prod 2026-10-14: 3 of 24 messages, all from tabs open for
 * hours, came from a session id never seen in `session:loaded`). AuthProvider's
 * effect does not rerun since Hector's user did not change, so identity is
 * re-pushed at each moment that leads to a message. Pushing is idempotent.
 */
const reassertCrispIdentity = (): void => {
  if (!crispExpectedEmail || crispIdentity === CRISP_ANONYMOUS || crispIdentity === 'unknown') return;
  pushCrispIdentity(crispIdentity, crispExpectedEmail, crispExpectedNickname);
};

/** Crisp's own events identify the browser session without recording legal message content. */
const registerCrispDiagnostics = (): void => {
  if (crispDiagnosticsRegistered || !isCrispLoaded()) return;
  crispDiagnosticsRegistered = true;

  // Source: https://docs.crisp.chat/guides/chatbox-sdks/web-sdk/dollar-crisp/ (verified 2026-10-13).
  // A queued command is not proof of delivery. These events let support compare
  // the browser's session with the Inbox when a visitor reports a lost message.
  pushToCrisp(['on', 'session:loaded', (sessionId: unknown) => {
    if (!isChatboxAuthorized || typeof sessionId !== 'string') return;
    captureCrispEvent('crisp_session_loaded', { crisp_session_id: sessionId });
    reassertCrispIdentity();
  }]);
  pushToCrisp(['on', 'chat:opened', () => {
    if (!isChatboxAuthorized) return;
    captureCrispEvent('crisp_chat_opened');
    reassertCrispIdentity();
  }]);
  // Le support ouvert à la demande sur fenêtre étroite redevient invisible à sa
  // fermeture, sans quoi la bulle resterait affichée jusqu'au rechargement.
  pushToCrisp(['on', 'chat:closed', () => {
    if (!isSupportRequested) return;
    isSupportRequested = false;
    syncCrispChatbox();
  }]);
  pushToCrisp(['on', 'message:sent', () => {
    if (!isChatboxAuthorized) return;
    try {
      const crisp = window.$crisp as unknown as { get?: (key: string, dataKey?: string) => unknown };
      const sessionId = crisp.get?.('session:identifier');
      const email = crisp.get?.('user:email');
      const userId = crisp.get?.('session:data', 'user_id');
      const emailMatchesAuth = typeof email === 'string' && email === crispExpectedEmail;
      captureCrispEvent('crisp_message_sent', {
        crisp_session_id: typeof sessionId === 'string' ? sessionId : null,
        email_matches_auth: emailMatchesAuth,
        user_id_matches_auth: typeof userId === 'string' && userId === crispIdentity,
      });
      // Last chance: the message already sits in an anonymous session, and
      // setting the e-mail now attaches that conversation to the lawyer.
      if (!emailMatchesAuth) reassertCrispIdentity();
    } catch {
      // Diagnostics must never interrupt a visitor's attempt to send a message.
    }
  }]);
};

const getLastCrispUserId = (): string | null => {
  try {
    return window.localStorage.getItem(CRISP_LAST_USER_KEY);
  } catch {
    return null;
  }
};

const setLastCrispUserId = (userId: string | null): void => {
  try {
    if (userId === null) window.localStorage.removeItem(CRISP_LAST_USER_KEY);
    else window.localStorage.setItem(CRISP_LAST_USER_KEY, userId);
  } catch {
    // A blocked storage API must not prevent an authenticated support session.
  }
};

/** Toast actions may run after navigation, before AuthProvider's next effect. */
export const isCrispSupportAvailable = (): boolean =>
  isChatboxAuthorized && !isPublicAuthPath(window.location.pathname);

/**
 * Réconcilie Crisp avec l'état d'authentification ET la page courante.
 * **Appelant unique : `AuthProvider`**, une fois la session résolue (jamais
 * pendant le bootstrap : `user` y vaut `null` pour tout le monde, et un
 * `session:reset` effacerait la conversation d'un avocat connecté à chaque
 * rechargement).
 *
 * Règles (signalement produit 2026-10-02 — le chat s'ouvrait par-dessus le
 * formulaire d'inscription avec « Votre email est invalide ») :
 *
 * 1. **Pas de support hors application.** Le canal n'est autorisé que si une
 *    session existe ET que la page n'est pas publique (`isPublicAuthPath`). Une
 *    session ne suffit pas : `/inscription` connecte le nouveau compte avant
 *    l'étape paiement, et une session périmée peut traîner dans le navigateur
 *    de quelqu'un qui vient s'inscrire.
 * 2. **Pas d'identité poussée hors application.** Sur une page publique on ne
 *    pousse ni e-mail ni nom : c'est l'e-mail d'un compte précédent, poussé au
 *    chargement de `/inscription`, que Crisp contestait dans le chat.
 *    L'identification a lieu à l'entrée dans l'application, une fois par compte.
 * 3. **Visiteur sans session = session Crisp vierge.** À la déconnexion, et au
 *    premier constat « personne n'est connecté » de la page, on pousse
 *    `session:reset` : l'identité du compte précédent ne fuit pas vers le
 *    visiteur suivant du même navigateur. Idem lors d'un passage direct d'un
 *    compte à un autre.
 * 4. **Arrivée chat fermé.** Quand le canal devient autorisé, on pousse
 *    `chat:close` AVANT `chat:show` : si Crisp a déplié la fenêtre pendant
 *    qu'elle était masquée (message automatique), l'avocat ne doit trouver que
 *    la bulle. Cela ne contredit pas la règle « jamais de `chat:close` avant de
 *    cacher » de `syncCrispChatbox` : ici personne n'est en conversation, la
 *    personne entre tout juste.
 *
 * On ne valide PAS le format de l'e-mail à la place de Crisp (sa règle n'est
 * pas connue) ; on s'interdit seulement d'en pousser un vide.
 */
export const syncCrispSupportSession = ({
  user,
  pathname,
}: {
  user: CrispSessionUser | null;
  pathname: string;
}): void => {
  const email = user?.email?.trim();
  const isAllowed = user !== null && Boolean(email) && !isPublicAuthPath(pathname);
  const previousNickname = crispExpectedNickname;
  crispExpectedEmail = isAllowed ? email ?? null : null;
  crispExpectedNickname = isAllowed && email
    ? user?.fullName?.trim() || email.split('@')[0] || null
    : null;
  let didReset = false;

  if (user === null) {
    if (crispIdentity !== CRISP_ANONYMOUS) {
      pushToCrisp(['do', 'session:reset']);
      crispIdentity = CRISP_ANONYMOUS;
      didReset = true;
    }
    setLastCrispUserId(null);
  } else if (isAllowed && email) {
    if (crispIdentity !== user.id) {
      // Crisp keeps the conversation in a cookie. A reload resets this module's
      // memory to "unknown", but must not discard the same user's open thread.
      // A different local user still needs a reset to prevent account crossover.
      // Source: https://docs.crisp.chat/guides/chatbox-sdks/web-sdk/session-continuity/ (verified 2026-10-13)
      if (crispIdentity !== CRISP_ANONYMOUS &&
          (crispIdentity !== 'unknown' || getLastCrispUserId() !== user.id)) {
        pushToCrisp(['do', 'session:reset']);
        didReset = true;
      }
      crispIdentity = user.id;
      setLastCrispUserId(user.id);

      pushCrispIdentity(user.id, email, crispExpectedNickname);
    } else if (crispExpectedNickname && crispExpectedNickname !== previousNickname) {
      // Même compte, nom changé dans Paramètres → Profil : le support doit lire le nouveau nom.
      pushToCrisp(['set', 'user:nickname', [crispExpectedNickname]]);
    }
    // Suivi de la page courante, pour que le support voie où se trouve l'avocat.
    pushToCrisp(['set', 'session:data', [[['current_url', window.location.href]]]]);
  }

  if (isAllowed && !isChatboxAuthorized) {
    pushToCrisp(['do', 'chat:close']);
  }

  // Une demande de support appartient au compte qui l'a faite : sans ce
  // retrait, le compte suivant du même onglet trouverait la bulle affichée.
  if (!isAllowed || didReset) isSupportRequested = false;
  isChatboxAuthorized = isAllowed;
  if (isAllowed) registerCrispDiagnostics();
  // Après un `session:reset`, on ne sait pas si Crisp conserve l'état masqué du
  // chatbox (non documenté, non vérifiable sans navigateur) : on ré-affirme
  // l'état voulu au lieu de se fier à celui qu'on avait mémorisé.
  syncCrispChatbox({ force: didReset });
};

/**
 * useCrisp — toutes les fonctions retournées sont **stables** entre renders.
 *
 * Pourquoi (incident 2026-06-21) : sans `useCallback`, chaque appel à
 * `useCrisp()` retournait un nouvel objet avec de nouvelles références. Tout
 * `useEffect` du parent qui mettait une de ces fonctions en dépendance voyait
 * la dep changer à chaque render et re-firait l'effet en boucle — sur
 * `BordereauPanel`, combiné aux autres setState d'init, ça atteignait la limite
 * de profondeur React.
 *
 * Garder ces refs stables est gratuit (aucun state interne) et évite ce piège
 * à TOUS les consommateurs sans avoir à mémoiser côté appelant.
 *
 * La visibilité du chatbox, elle, n'est PAS exposée ici : elle passe par
 * `useCrispChatboxHidden` / `syncCrispSupportSession` ci-dessus (un seul
 * arbitre, cf. leur docstring), tout comme l'identification et la
 * réinitialisation de session.
 */

export const useCrisp = () => {
  /**
   * Ouvre le chat Crisp
   */
  const openChat = useCallback(() => {
    if (!isCrispLoaded() || !isCrispSupportAvailable()) return;
    // Sur fenêtre étroite la bulle est masquée : `chat:open` ouvrirait une
    // fenêtre invisible (Crisp : « Hides the chatbox (don't close it if open) »).
    isSupportRequested = true;
    syncCrispChatbox();
    pushToCrisp(['do', 'chat:open']);
  }, []);

  /**
   * Ferme le chat Crisp
   */
  const closeChat = useCallback(() => {
    if (!isCrispLoaded()) return;
    pushToCrisp(['do', 'chat:close']);
  }, []);

  /**
   * Envoie un message automatique dans le chat
   */
  const sendMessage = useCallback((message: string) => {
    if (!isCrispLoaded()) return;
    pushToCrisp(['do', 'message:send', ['text', message]]);
  }, []);

  /**
   * Définit un segment/tag pour l'utilisateur
   */
  const setSegments = useCallback((segments: string[]) => {
    if (!isCrispLoaded()) return;
    pushToCrisp(['set', 'session:segments', segments]);
  }, []);

  /**
   * Ajoute un événement au timeline de l'utilisateur
   */
  const trackEvent = useCallback((eventName: string, eventData?: Record<string, unknown>) => {
    if (!isCrispLoaded()) return;

    const eventPayload = eventData
      ? { text: eventName, data: eventData }
      : { text: eventName };

    pushToCrisp(['set', 'session:event', eventPayload]);
  }, []);

  return {
    openChat,
    closeChat,
    sendMessage,
    setSegments,
    trackEvent,
  };
};

export type { CrispSessionUser };

